export type NormalizePhoneNumberOptions = {
  defaultCountryCallingCode?: string
}

export function normalizeCountryCallingCode(countryCallingCode: string): string {
  const digits = `${countryCallingCode || ''}`.replace(/\D/g, '')

  if (digits.startsWith('00')) {
    return digits.slice(2)
  }

  return digits
}

export function normalizePhoneNumber(phoneNumber: any, options: NormalizePhoneNumberOptions = {}): string {
  const rawPhoneNumber = `${phoneNumber || ''}`.trim()
  const phoneDigits = rawPhoneNumber.replace(/\D/g, '')

  if (!phoneDigits) {
    return ''
  }

  const hasLeadingPlus = rawPhoneNumber.startsWith('+')

  if (hasLeadingPlus) {
    if (phoneDigits.startsWith('00')) {
      const internationalDigits = phoneDigits.slice(2)
      return internationalDigits ? `+${internationalDigits}` : ''
    }

    return `+${phoneDigits}`
  }

  if (phoneDigits.startsWith('00')) {
    const internationalDigits = phoneDigits.slice(2)
    return internationalDigits ? `+${internationalDigits}` : ''
  }

  if (rawPhoneNumber.match(/^49\s/)) {
    return `+${phoneDigits}`
  }

  const defaultCountryDigits = normalizeCountryCallingCode(options.defaultCountryCallingCode)

  if (!defaultCountryDigits) {
    return phoneDigits
  }

  let normalizedDigits = phoneDigits

  if (phoneDigits.startsWith(defaultCountryDigits)) {
    normalizedDigits = phoneDigits
  } else if (phoneDigits.startsWith('0')) {
    normalizedDigits = `${defaultCountryDigits}${phoneDigits.slice(1)}`
  } else {
    normalizedDigits = `${defaultCountryDigits}${phoneDigits}`
  }

  if (normalizedDigits.startsWith(`${defaultCountryDigits}0`)) {
    normalizedDigits = `${defaultCountryDigits}${normalizedDigits.slice(defaultCountryDigits.length + 1)}`
  }

  return `+${normalizedDigits}`
}

export function normalizeEmail(value: string): string {
  const trimmed = `${value || ''}`.trim()
  const parts = trimmed.split('@')

  if (parts.length !== 2) {
    return ''
  }

  const [localPart, domainPart] = parts

  if (!EMAIL_LOCAL_PART_REGEX.test(localPart)) {
    return ''
  }

  const asciiDomain = domainToAscii(domainPart)

  if (!EMAIL_DOMAIN_REGEX.test(asciiDomain)) {
    return ''
  }

  return `${localPart}@${asciiDomain}`
}

export function normalizeIban(value: string): string {
  const iban = transliterateToAscii(`${value || ''}`)
    .replace(/[^a-zA-Z0-9]+/g, '')
    .toUpperCase()

  if (!IBAN_REGEX.test(iban)) {
    return ''
  }

  if (!isValidIbanChecksum(iban)) {
    return ''
  }

  return iban
}

const EMAIL_LOCAL_PART_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+$/
const EMAIL_DOMAIN_REGEX = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/
const IBAN_REGEX = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/
const GERMAN_TRANSLITERATION: { [key: string]: string } = {
  '\u00c4': 'Ae',
  '\u00d6': 'Oe',
  '\u00dc': 'Ue',
  '\u00e4': 'ae',
  '\u00f6': 'oe',
  '\u00fc': 'ue',
  '\u00df': 'ss',
}

function domainToAscii(domain: string) {
  try {
    return new URL(`https://${domain.trim().toLowerCase()}/`).hostname
  } catch (error) {
    return ''
  }
}

function transliterateToAscii(value: string) {
  return value
    .replace(/[\u00c4\u00d6\u00dc\u00e4\u00f6\u00fc\u00df]/g, char => GERMAN_TRANSLITERATION[char] || char)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
}

function isValidIbanChecksum(iban: string) {
  const rearranged = `${iban.slice(4)}${iban.slice(0, 4)}`
  let remainder = 0

  for (const char of rearranged) {
    const charCode = char.charCodeAt(0)
    const digits = charCode >= 65 && charCode <= 90 ? String(charCode - 55) : char

    for (const digit of digits) {
      remainder = (remainder * 10 + Number(digit)) % 97
    }
  }

  return remainder === 1
}
